/** @jsxImportSource @emotion/react */

import { useTokens } from '../../../../deps/@bshgroup/component-themes';
import React, { ReactNode } from 'react';
import { Hint } from '../Hint/Hint';
import { Chip } from '../Chip/Chip';
import { CSSProp } from '../../../types/emotion';
import { LinearLayout } from '../LinearLayout/LinearLayout';
import { Text } from '../Text/Text';

export interface DropdownProps {
  /**
   * The id of the dropdown, needs to be the name of the property of the form
   */
  id?: string;
  /**
   * The value of the dropdown
   */
  value?: string;
  /**
   * The values of the dropdown (when multiple)
   */
  values?: string[];
  /**
   * The options to be displayed in the dropdown
   */
  options?: Array<{ id: string | number; displayName: string; disabled?: boolean }>;
  /**
   * List of `<option>` elements as children in case the options property is not used
   */
  children?: ReactNode;
  /**
   * The label of the dropdown
   */
  label?: ReactNode;
  /**
   * The placeholder of the dropdown
   */
  placeholder?: string;
  /**
   * The value when the placeholder is selected
   */
  placeholderValue?: string;
  /**
   * Whether the dropdown value is required
   */
  required?: boolean;
  /**
   * Error message
   */
  errorMessage?: string;
  /**
   * Whether the dropdown is disabled
   */
  disabled?: boolean;
  /**
   * The autocomplete attribute of the dropdown
   */
  autocomplete?: string;
  /**
   * Allow multiple selection from Dropdown
   */
  multiple?: boolean;
  /**
   * The variant of the dropdown
   */
  variant?: 'default' | 'chip';
  /**
   * Function to handle change event
   */
  onChange?: (value: string | string[]) => void;
  /**
   * Function to handle focus event
   */
  onFocus?: () => Promise<void>;
  /**
   * Function to handle blur event
   */
  onBlur?: () => Promise<void>;
  /**
   * Adapt the background color
   */
  backgroundColor?: string;
}

/**
 * A dropdown component to be used inside a `FormContext`
 */
export function Dropdown({
  id,
  value,
  values,
  options,
  children,
  label,
  placeholder,
  placeholderValue,
  required,
  errorMessage,
  disabled,
  autocomplete,
  multiple = false,
  variant = 'default',
  onChange,
  onFocus,
  onBlur,
  backgroundColor,
  ...props
}: DropdownProps & Omit<Partial<React.HTMLAttributes<HTMLDivElement>>, keyof DropdownProps> & CSSProp) {
  const tokens = useTokens();

  return (
    <>
      <LinearLayout
        spacing="tiny"
        orientation="horizontal"
        align="center"
        css={{
          backgroundColor: backgroundColor ?? tokens.colors['secondary-base-200'],
          height: variant === 'chip' ? '2rem' : '3rem',
          ...(variant === 'chip' ? { padding: '0 1rem', borderRadius: '1rem' } : {}),
        }}
        {...props}
      >
        {label && variant === 'chip' && (typeof label === 'string' ? <Text>{label}</Text> : label)}
        <div css={{ width: '100%' }}>
          <div
            css={{
              position: 'relative',
              borderBottom: variant === 'chip' ? 'none' : '1px solid black',
              '&::after': {
                backgroundImage:
                  'url("data:image/svg+xml;base64,PHN2ZyBjbGFzcz0iY3NzLTF3ZW9oNXgiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBmaWxsLXJ1bGU9ImV2ZW5vZGQiIGNsaXAtcnVsZT0iZXZlbm9kZCIgZD0iTTIxLjAwMDIgOC4wMDAyNUMyMS4wMDAyIDguMjU2MjUgMjAuOTAyMyA4LjUxMjI1IDIwLjcwNzMgOC43MDcyNUwxMi43MDcyIDE2LjcwNzJDMTIuMzE2MiAxNy4wOTgyIDExLjY4NDIgMTcuMDk4MiAxMS4yOTMzIDE2LjcwNzJMMy4yOTMyNSA4LjcwNzI1QzIuOTAyMjUgOC4zMTYyNSAyLjkwMjI1IDcuNjg0MjUgMy4yOTMyNSA3LjI5MzI1QzMuNjg0MjUgNi45MDIyNSA0LjMxNjI1IDYuOTAyMjUgNC43MDcyNSA3LjI5MzI1TDEyLjAwMDIgMTQuNTg2MkwxOS4yOTMyIDcuMjkzMjVDMTkuNjg0MiA2LjkwMjI1IDIwLjMxNjIgNi45MDIyNSAyMC43MDczIDcuMjkzMjVDMjAuOTAyMyA3LjQ4ODI1IDIxLjAwMDIgNy43NDQyNSAyMS4wMDAyIDguMDAwMjVaIiBmaWxsPSJjdXJyZW50Q29sb3IiPjwvcGF0aD48L3N2Zz4=")',
                backgroundPosition: 'right 10px center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'auto 80%',
                content: '""',
                height: '1.5rem',
                pointerEvents: 'none',
                position: 'absolute',
                right: variant === 'chip' ? '-0.75rem' : '.7rem',
                top: variant === 'chip' ? '.35rem' : '.75rem',
                width: '2rem',
              },
            }}
          >
            {label && variant === 'default' && (
              <label
                htmlFor={id}
                css={{
                  overflow: 'hidden',
                  position: 'absolute',
                  textOverflow: 'ellipsis',
                  padding: '.25rem 1rem 0',
                  fontFamily: tokens.styles['dropdown-content-list-item-label-initial-fontFamily'],
                  fontSize: '.75rem',
                  fontWeight: tokens.styles['dropdown-content-list-item-label-initial-fontWeight'],
                  whiteSpace: 'nowrap',
                  width: '100%',
                }}
              >
                {label}
                {required && ' *'}
              </label>
            )}
            <select
              css={{
                appearance: 'none',
                backgroundColor: backgroundColor ?? tokens.colors['secondary-base-200'],
                height: variant === 'chip' ? '2rem' : '3rem',
                overflow: 'hidden',
                padding: variant === 'chip' ? 0 : '0 2.75rem 0 1rem',
                textOverflow: 'ellipsis',
                border: 'none',
                width: '100%',
                marginRight: variant === 'chip' ? '1.5rem' : 0,
                fontFamily: tokens.styles['dropdown-content-list-item-label-initial-fontFamily'],
                fontSize:
                  variant === 'chip' ? '1rem' : tokens.styles['dropdown-content-list-item-label-initial-fontSize'],
                fontWeight: tokens.styles['dropdown-content-list-item-label-initial-fontWeight'],
                cursor: 'pointer',
                outline: 'none',
                ...(label && variant === 'default' ? { paddingTop: '1.125rem', paddingBottom: '.3125rem' } : {}),
              }}
              id={id}
              value={multiple ? '' : value}
              onChange={e => onChange?.(e.target.value)}
              onFocus={onFocus}
              onBlur={onBlur}
              required={required}
              disabled={disabled}
              autoComplete={autocomplete}
              aria-describedby={`${id}-error`}
            >
              {placeholder && (
                <option value={multiple ? '' : placeholderValue} disabled>
                  {placeholder}
                </option>
              )}
              {options?.map(item => (
                <option key={item.id} value={item.id} disabled={item.disabled}>
                  {item.displayName}
                </option>
              )) ?? children}
            </select>
            {errorMessage && <Hint id={`${id}-error`} level="error" description={errorMessage} aria-live="polite" />}
          </div>
        </div>
      </LinearLayout>
      {multiple &&
        values?.map((value: string) => (
          <Chip key={value} onRemove={() => onChange?.(values?.filter((e: string) => e !== value))}>
            {options?.find(e => e.id === value)?.displayName}
          </Chip>
        ))}
    </>
  );
}
